import * as THREE from 'three';

/**
 * Triangulate a 3D polygon ring (any orientation) into index triples.
 *
 * A fan from vertex 0 only works for convex rings: on a concave outline the
 * fan triangles cross the concave notches and spill outside the polygon,
 * drawing a sawtooth fill (same bug StockpileVisualization hit). Instead we
 * project the ring onto the coordinate plane its Newell normal faces most and
 * let earcut (via ShapeUtils) triangulate it, which is concave-safe. Indices
 * refer to the input ring, so callers keep the real 3D vertex positions.
 */
export const triangulatePolygon = (points: THREE.Vector3[]): number[][] => {
  if (points.length < 3) return [];

  // Newell normal, relative to the first vertex to stay precise at UTM scale.
  const o = points[0];
  const normal = new THREE.Vector3();
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const ay = a.y - o.y, az = a.z - o.z, ax = a.x - o.x;
    const by = b.y - o.y, bz = b.z - o.z, bx = b.x - o.x;
    normal.x += (ay - by) * (az + bz);
    normal.y += (az - bz) * (ax + bx);
    normal.z += (ax - bx) * (ay + by);
  }
  if (normal.lengthSq() === 0) return [];

  // Drop the axis the polygon faces most (Z for ground polygons, so they are
  // triangulated in plan). Projecting onto the tilted best-fit plane instead
  // lets vertex height noise push a near-collinear reflex vertex across its
  // neighbours' line, and earcut then emits a triangle that is inverted in
  // plan and double-blends over the fill as a bright sliver.
  const nx = Math.abs(normal.x), ny = Math.abs(normal.y), nz = Math.abs(normal.z);
  const contour = points.map((p) => {
    const dx = p.x - o.x, dy = p.y - o.y, dz = p.z - o.z;
    if (nz >= nx && nz >= ny) return new THREE.Vector2(dx, dy);
    if (ny >= nx) return new THREE.Vector2(dx, dz);
    return new THREE.Vector2(dy, dz);
  });

  return THREE.ShapeUtils.triangulateShape(contour, []);
};

/**
 * Build a fill mesh geometry for a polygon ring. Positions are relative to
 * `origin` (place the mesh at `origin`) to avoid Float32 precision loss.
 */
export const buildPolygonFillGeometry = (
  points: THREE.Vector3[],
  origin: THREE.Vector3,
): THREE.BufferGeometry | null => {
  const triangles = triangulatePolygon(points);
  if (triangles.length === 0) return null;

  const positions = new Float32Array(triangles.length * 9);
  let k = 0;
  for (const tri of triangles) {
    for (const idx of tri) {
      positions[k++] = points[idx].x - origin.x;
      positions[k++] = points[idx].y - origin.y;
      positions[k++] = points[idx].z - origin.z;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
};
